import React, { useState } from 'react';
import { Button, Modal, Form, Input, Table, Space, Tag, Switch, Typography, Row, Col, DatePicker } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, EnvironmentOutlined, BuildOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import apiService from '../../../services/api';
import { menteeProfileQueryKeys } from '../../../lib/queryClient';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';
import { MenteeWorkExperienceResponse, CreateMenteeWorkExperienceRequest, UpdateMenteeWorkExperienceRequest } from '../../../types';

const { Title } = Typography;
const { TextArea } = Input;

const WorkExperienceTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingWorkExperience, setEditingWorkExperience] = useState<MenteeWorkExperienceResponse | null>(null);
  const [form] = Form.useForm();

  const { data: workExperiences = [], isLoading: loading } = useQuery({
    queryKey: menteeProfileQueryKeys.workExperiences,
    queryFn: async () => {
      const res = await apiService.getMenteeWorkExperiences();
      if (!res.success) throw new Error(res.message || 'İş deneyimleri yüklenemedi.');
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateMenteeWorkExperienceRequest) => apiService.createMenteeWorkExperience(data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('İş deneyimi başarıyla eklendi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.workExperiences });
        setModalVisible(false);
        form.resetFields();
      } else toast.error(res.message || 'İş deneyimi eklenemedi.');
    },
    onError: () => toast.error('İş deneyimleri yüklenirken hata oluştu.'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMenteeWorkExperienceRequest }) =>
      apiService.updateMenteeWorkExperience(id, data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('İş deneyimi başarıyla güncellendi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.workExperiences });
        setModalVisible(false);
        setEditingWorkExperience(null);
        form.resetFields();
      } else toast.error(res.message || 'İş deneyimi güncellenemedi.');
    },
    onError: () => toast.error('İş deneyimi güncellenirken hata oluştu.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteMenteeWorkExperience(id),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('İş deneyimi başarıyla silindi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.workExperiences });
      } else toast.error(res.message || 'İş deneyimi silinemedi.');
    },
    onError: () => toast.error('İş deneyimi silinirken hata oluştu.'),
  });

  const handleAdd = () => {
    setEditingWorkExperience(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (workExperience: MenteeWorkExperienceResponse) => {
    setEditingWorkExperience(workExperience);
    form.setFieldsValue({
      companyName: workExperience.companyName,
      position: workExperience.position,
      description: workExperience.description,
      startDate: dayjs(workExperience.startDate),
      endDate: workExperience.endDate ? dayjs(workExperience.endDate) : null,
      isCurrentJob: workExperience.isCurrentJob,
      industry: workExperience.industry,
      achievements: workExperience.achievements
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const isCurrent = !!values.isCurrentJob;
      const workExperienceData = {
        companyName: values.companyName,
        position: values.position,
        description: values.description,
        startDate: values.startDate.format('YYYY-MM-DD'),
        endDate: isCurrent ? null : (values.endDate ? values.endDate.format('YYYY-MM-DD') : null),
        isCurrentJob: isCurrent,
        location: values.location,
        industry: values.industry,
        achievements: values.achievements
      };

      if (editingWorkExperience) {
        updateMutation.mutate({ id: editingWorkExperience.id, data: workExperienceData });
      } else {
        createMutation.mutate(workExperienceData);
      }
    } catch (error) {
      // form validation
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingWorkExperience(null);
    form.resetFields();
  };

  const handleDelete = (id: number) => deleteMutation.mutate(id);

  // Filtreleme
  const filteredWorkExperiences = workExperiences.filter(workExp =>
    workExp.companyName.toLowerCase().includes(searchText.toLowerCase()) ||
    workExp.position.toLowerCase().includes(searchText.toLowerCase()) ||
    (workExp.industry && workExp.industry.toLowerCase().includes(searchText.toLowerCase()))
  );

  const columns = [
    {
      title: 'Şirket',
      dataIndex: 'companyName',
      key: 'companyName',
      sorter: (a: MenteeWorkExperienceResponse, b: MenteeWorkExperienceResponse) => a.companyName.localeCompare(b.companyName),
    },
    {
      title: 'Pozisyon',
      dataIndex: 'position',
      key: 'position',
      sorter: (a: MenteeWorkExperienceResponse, b: MenteeWorkExperienceResponse) => a.position.localeCompare(b.position),
    },
    {
      title: 'Sektör',
      dataIndex: 'industry',
      key: 'industry',
      render: (industry: string) => industry ? <Tag color="blue">{industry}</Tag> : '-',
    },
    {
      title: 'Lokasyon',
      dataIndex: 'location',
      key: 'location',
      render: (location: string) => location ? (
        <span>
          <EnvironmentOutlined style={{ marginRight: '4px', color: 'var(--text-secondary)' }} />
          {location}
        </span>
      ) : '-',
    },
    {
      title: 'Başlangıç',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string) => dayjs(date).format('MM/YYYY'),
      sorter: (a: MenteeWorkExperienceResponse, b: MenteeWorkExperienceResponse) => 
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    },
    {
      title: 'Bitiş',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date: string, record: MenteeWorkExperienceResponse) => {
        if (record.isCurrentJob || !date) {
          return <Tag color="green">Günümüz</Tag>;
        }
        return dayjs(date).format('MM/YYYY');
      },
    },
    {
      title: 'Durum',
      dataIndex: 'isCurrentJob',
      key: 'isCurrentJob',
      render: (isCurrentJob: boolean) => (
        <Tag color={isCurrentJob ? 'green' : 'default'}>
          {isCurrentJob ? 'Mevcut İş' : 'Geçmiş İş'}
        </Tag>
      ),
      filters: [
        { text: 'Mevcut İş', value: true },
        { text: 'Geçmiş İş', value: false }
      ],
      onFilter: (value: boolean | React.Key, record: MenteeWorkExperienceResponse) => record.isCurrentJob === value,
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: MenteeWorkExperienceResponse) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: 'var(--text-secondary)' }}
          >
            Düzenle
          </Button>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Sil
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
    <PanelTabCard title="İş Hayatım" description="İş deneyimlerinizi ekleyin ve güncelleyin.">
        <TabListLoading loading={loading}>
        <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
          <Col>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{
                backgroundColor: 'var(--text-secondary)',
                borderColor: 'var(--text-secondary)',
                fontWeight: 500
              }}
            >
              Yeni İş Deneyimi
            </Button>
          </Col>
        </Row>
        
        <Row style={{ marginBottom: '16px' }}>
          <Col span={8}>
            <Input
              placeholder="Şirket, pozisyon, sektör veya lokasyon ara..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ borderRadius: '6px' }}
            />
          </Col>
        </Row>

        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={filteredWorkExperiences}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} iş deneyimi`
          }}
        />
        </TabListLoading>
    </PanelTabCard>

      <Modal
        title={editingWorkExperience ? 'İş Deneyimi Düzenle' : 'Yeni İş Deneyimi'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={700}
        okText={editingWorkExperience ? 'Güncelle' : 'Ekle'}
        cancelText="İptal"
        okButtonProps={{
          style: { backgroundColor: 'var(--text-secondary)', borderColor: 'var(--text-secondary)' }
        }}
      >
        <Form layout="vertical" form={form} requiredMark={false}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="companyName" 
                label="Şirket Adı" 
                rules={[{ required: true, message: 'Şirket adı gerekli' }]}
              >
                <Input placeholder="Örn: Microsoft, Google" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="position" 
                label="Pozisyon" 
                rules={[{ required: true, message: 'Pozisyon gerekli' }]}
              >
                <Input placeholder="Örn: Frontend Developer" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="industry" 
                label="Sektör"
              >
                <Input placeholder="Örn: Teknoloji, Finans" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="location" 
                label="Lokasyon"
              >
                <Input placeholder="Örn: İstanbul, Türkiye" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="startDate" 
                label="Başlangıç Tarihi" 
                rules={[
                  { required: true, message: 'Başlangıç tarihi gerekli' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const today = dayjs().startOf('month');
                      const selectedDate = dayjs(value).startOf('month');
                      if (selectedDate.isAfter(today)) {
                        return Promise.reject(new Error('Başlangıç tarihi bugünden sonra olamaz'));
                      }
                      const endDate = form.getFieldValue('endDate');
                      if (endDate && !form.getFieldValue('isCurrentJob') && selectedDate.isAfter(dayjs(endDate).startOf('month'))) {
                        return Promise.reject(new Error('Başlangıç tarihi bitiş tarihinden sonra olamaz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  picker="month"
                  placeholder="Başlangıç"
                  disabledDate={(current) => current && current.isAfter(dayjs(), 'month')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="endDate" 
                label="Bitiş Tarihi"
                dependencies={['startDate', 'isCurrentJob']}
                rules={[
                  {
                    validator: (_, value) => {
                      if (form.getFieldValue('isCurrentJob')) return Promise.resolve();
                      if (!value) return Promise.resolve();
                      const startDate = form.getFieldValue('startDate');
                      if (startDate && dayjs(value).startOf('month').isBefore(dayjs(startDate).startOf('month'))) {
                        return Promise.reject(new Error('Bitiş tarihi başlangıç tarihinden önce olamaz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  picker="month"
                  placeholder="Bitiş"
                  disabled={form.getFieldValue('isCurrentJob')}
                  disabledDate={(current) => {
                    if (form.getFieldValue('isCurrentJob')) return false;
                    const startDate = form.getFieldValue('startDate');
                    if (startDate && current) {
                      return current.isBefore(dayjs(startDate), 'month');
                    }
                    return false;
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="isCurrentJob" label="Mevcut İş" valuePropName="checked" initialValue={false}>
            <Switch
              onChange={(checked) => {
                if (checked) form.setFieldsValue({ endDate: null });
              }}
            />
          </Form.Item>

          <Form.Item 
            name="description" 
            label="İş Açıklaması"
          >
            <TextArea 
              rows={3} 
              placeholder="Bu pozisyonda yaptığınız işler..."
            />
          </Form.Item>

          <Form.Item 
            name="achievements" 
            label="Başarılar"
          >
            <TextArea 
              rows={3} 
              placeholder="Bu pozisyonda elde ettiğiniz başarılar..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default WorkExperienceTab;
